// PackingScanXL - Sprint 44 Fase 3 Vista 7.
// Pantalla de escaneo packing. Header + barra progreso + form de caja
// (codigo + peso) + lista lineas + ScanInputXL + boton cerrar caja
// (que dispara kardex SALIDA backend) + boton imprimir etiqueta.
import { useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import usePackingScanXL from './hooks/usePackingScanXL';
import ScanInputXL from '../../components/shared/ScanInputXL';
import ScanLineaRowXL from '../../components/shared/ScanLineaRowXL';
import XLBadge from '../../components/xl/XLBadge';
import XLInput from '../../components/xl/XLInput';
import XLSkeleton from '../../components/xl/XLSkeleton';

const CSS_ID = 'xl-pack-scan-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-hdr { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-hdr__left { display: flex; align-items: center; gap: 14px; min-width: 0 }
.xl-hdr__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-hdr__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-hdr__back:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-hdr__title { margin: 0; font-size: 20px; font-weight: 600; line-height: 1.2 }
.xl-hdr__sub { margin: 2px 0 0; font-size: 12px; color: var(--xl-color-text-secondary) }
.xl-hdr__sub b { color: var(--xl-color-text) }
.xl-hdr__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap }

.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn--info { background: var(--xl-color-info); color: var(--xl-color-text-inverse) }
.xl-btn--info:hover { filter: brightness(1.08) }
.xl-btn--success { background: var(--xl-color-success); color: var(--xl-color-text-inverse) }
.xl-btn--success:hover { filter: brightness(1.08) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }

.xl-flash {
  margin-bottom: 12px; padding: 10px 14px;
  border-radius: var(--xl-radius-md);
  font-size: 14px; font-weight: 500; text-align: center;
}
.xl-flash--ok  { background: rgba(5,150,105,0.15); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.32) }
.xl-flash--err { background: rgba(220,38,38,0.15); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.32) }

.xl-progress-card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  padding: 14px 16px;
  margin-bottom: 16px;
}
.xl-progress-card__row {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 13px; color: var(--xl-color-text-secondary);
  margin-bottom: 8px;
}
.xl-progress-card__counter {
  font-family: var(--xl-font-mono); font-weight: 600;
  color: var(--xl-color-text);
}
.xl-progress-card__bar {
  width: 100%; height: 8px;
  background: var(--xl-color-surface-alt);
  border-radius: var(--xl-radius-full);
  overflow: hidden;
}
.xl-progress-card__bar-fill {
  height: 100%; border-radius: inherit;
  transition: width 220ms cubic-bezier(0.4,0,0.2,1);
  background: var(--xl-color-info);
}
.xl-progress-card__bar-fill--full { background: var(--xl-color-success) }

.xl-caja-card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  padding: 14px 16px;
  margin-bottom: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  align-items: end;
}
@media (max-width: 800px) { .xl-caja-card { grid-template-columns: 1fr } }
.xl-caja-card__hint {
  font-size: 11px;
  color: var(--xl-color-text-muted);
}

.xl-rows { display: flex; flex-direction: column; gap: 10px }
.xl-rows__empty {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  padding: 40px 16px;
  text-align: center;
  color: var(--xl-color-text-muted);
  font-size: 13px;
}
.xl-loading { display: flex; flex-direction: column; gap: 10px }
`;

const ESTADO_VARIANT = {
  pendiente:  'neutral',
  en_proceso: 'info',
  completado: 'success',
};
const ESTADO_LABEL = {
  pendiente:  'Pendiente',
  en_proceso: 'En proceso',
  completado: 'Completado',
};

export default function PackingScanXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const sc = usePackingScanXL();
  const [cajaCodigo, setCajaCodigo] = useState('');
  const [peso, setPeso] = useState('');

  useEffect(() => {
    if (sc.packing) {
      setCajaCodigo(sc.packing.caja_codigo || '');
      setPeso(sc.packing.peso_kg == null ? '' : sc.packing.peso_kg);
    }
  }, [sc.packing]);

  if (!sc.loaded || !sc.packing) {
    return (
      <div className="xl-page">
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="line" width="40%" height={20} />
          <XLSkeleton variant="rect" height={80} />
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={60} />
        </div>
      </div>
    );
  }

  const p = sc.packing;
  const lineas = p.lineas || [];
  const totalLineas = lineas.length;
  const completas = lineas.filter((l) => l.estado === 'completo').length;
  const pct = totalLineas ? Math.round((completas / totalLineas) * 100) : 0;
  const canComplete = totalLineas > 0 && completas === totalLineas && p.estado !== 'completado';
  const fillCls = 'xl-progress-card__bar-fill' + (pct === 100 ? ' xl-progress-card__bar-fill--full' : '');
  const editable = p.estado !== 'completado';

  return (
    <div className="xl-page">
      <header className="xl-hdr">
        <div className="xl-hdr__left">
          <button type="button" className="xl-hdr__back"
            onClick={() => sc.navigate('/packing')}>
            {'\u2190 Volver'}
          </button>
          <div>
            <h2 className="xl-hdr__title">
              Packing #{p.id}
              {p.picking && ' \u00b7 ' + p.picking.venta_numero}
            </h2>
            <p className="xl-hdr__sub">
              Cliente: <b>{(p.picking && p.picking.cliente_nombre) || '\u2014'}</b>
              {' \u00b7 '}
              <XLBadge variant={ESTADO_VARIANT[p.estado] || 'neutral'} dot>
                {ESTADO_LABEL[p.estado] || p.estado}
              </XLBadge>
            </p>
          </div>
        </div>
        <div className="xl-hdr__actions">
          {editable && (
            <button type="button" className="xl-btn xl-btn--info"
              onClick={() => sc.setScanning(true)}>
              Activar escaner
            </button>
          )}
          {editable && (
            <button type="button" className="xl-btn xl-btn--success"
              onClick={sc.onCerrar}
              disabled={sc.completing || !canComplete}
              title={!canComplete ? 'Completa todos los items primero' : ''}>
              {sc.completing ? 'Cerrando\u2026' : 'Cerrar caja'}
            </button>
          )}
          {p.estado === 'completado' && (
            <button type="button" className="xl-btn xl-btn--ghost"
              onClick={sc.onImprimirEtiqueta}>
              Imprimir etiqueta
            </button>
          )}
        </div>
      </header>

      {sc.flash && (
        <div className={'xl-flash ' + (sc.flash.kind === 'ok' ? 'xl-flash--ok' : 'xl-flash--err')}
          role={sc.flash.kind === 'ok' ? 'status' : 'alert'}>
          {sc.flash.text}
        </div>
      )}

      <div className="xl-progress-card">
        <div className="xl-progress-card__row">
          <span>Progreso</span>
          <span className="xl-progress-card__counter">
            {completas} / {totalLineas} \u00b7 {pct}%
          </span>
        </div>
        <div className="xl-progress-card__bar" aria-hidden="true">
          <div className={fillCls} style={{ width: pct + '%' }} />
        </div>
      </div>

      {editable && (
        <div className="xl-caja-card">
          <XLInput
            label="Codigo de caja"
            value={cajaCodigo}
            onChange={(e) => setCajaCodigo(e.target.value)}
            onBlur={() => sc.onGuardarCaja({ caja_codigo: cajaCodigo })}
            placeholder="CAJA-001"
          />
          <XLInput
            label="Peso (kg)"
            type="number"
            min="0"
            step="0.001"
            value={peso}
            onChange={(e) => setPeso(e.target.value)}
            onBlur={() => sc.onGuardarCaja({ peso_kg: peso === '' ? null : Number(peso) })}
            placeholder="0.5"
          />
          <div className="xl-caja-card__hint">
            Dimensiones opcionales (largo, ancho, alto) via API.
          </div>
        </div>
      )}

      <div className="xl-rows">
        {lineas.length === 0 && (
          <div className="xl-rows__empty">Sin lineas.</div>
        )}
        {lineas.map((l) => (
          <ScanLineaRowXL key={l.id} linea={l}
            field_req="cantidad_requerida" field_ok="cantidad_empacada" />
        ))}
      </div>

      <ScanInputXL
        active={sc.scanning}
        onScan={(code) => { sc.onScan(code); }}
        placeholder="Escanea SKU al empacar"
      />
    </div>
  );
}
